import { deleteRequest, get, patch, post, put, webApi } from '../utils/http';
import axios from 'axios';
import queryString from 'query-string';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import settings from '../settings';
dayjs.extend(utc);
dayjs.extend(timezone);

// NW-2574: cancel the previous in-flight persons page fetch so rapid pagination
// clicks don't pile up requests — only the latest fetch completes.
let pageFetchCancelSource;
const nextPageFetchCancelToken = () => {
  if (pageFetchCancelSource) {
    pageFetchCancelSource.cancel('superseded-by-newer-persons-fetch');
  }
  pageFetchCancelSource = axios.CancelToken.source();
  return pageFetchCancelSource.token;
};

// Helper function to ensure dates are in ISO format
const ensureISOFormat = (dateString) => {
  if (!dateString) return dateString;

  // Check if it's already in ISO format (contains 'T' and 'Z' or timezone offset)
  const isoRegex =
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?([+-]\d{2}:\d{2}|Z)?$/;
  if (isoRegex.test(dateString)) {
    return dateString;
  }

  // Convert to ISO format using dayjs
  const date = dayjs(dateString);
  if (date.isValid()) {
    return date.toISOString();
  }

  // Return original if invalid
  return dateString;
};

// Helper function to normalize date fields in filter object
const normalizeDateFields = (filter) => {
  if (!filter) return filter;

  const normalizedFilter = { ...filter };

  if (normalizedFilter.start_range) {
    // Set start_range to 00:00:00 (beginning of the day)
    // Use format() instead of toISOString() to preserve local timezone
    const startDate = dayjs(normalizedFilter.start_range)
      .startOf('day')
      .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
    normalizedFilter.start_range = startDate;
  }

  if (normalizedFilter.end_range) {
    // Set end_range to 23:59:59 (end of the day)
    // Use format() instead of toISOString() to preserve local timezone
    const endDate = dayjs(normalizedFilter.end_range)
      .endOf('day')
      .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
    normalizedFilter.end_range = endDate;
  }

  return normalizedFilter;
};

export const services = {
  getPerson: async (filter, searchFilter) => {
    return get(webApi + `api/results/${filter.code}${searchFilter.toString()}`);
  },
  // NOTE: getPersons intentionally does NOT use the page-fetch cancel token —
  // it is shared with the LOAD_ALL action (bulk "select all" loads every person
  // via getPersons). Cancelling here would abort a bulk-load when the user
  // paginates. Only filterPersons (the paginated api/results/filter path) cancels.
  getPersons: async (filter, searchFilter, isINES) => {
    const normalizedFilter = normalizeDateFields(filter);

    if (isINES) {
      return get(
        settings.webApiCore +
          `/api/ines/query/persons${searchFilter.toString()}&${queryString.stringify(
            normalizedFilter,
          )}`,
      );
    }

    return get(
      webApi +
        `api/persons${searchFilter.toString()}&${queryString.stringify(
          normalizedFilter,
        )}`,
    );
  },
  filterPersons: async (filter, searchFilter, isINES = false) => {
    const normalizedFilter = normalizeDateFields(filter);
    const cancelToken = nextPageFetchCancelToken();

    if (isINES) {
      return get(
        settings.webApiCore +
          `/api/ines/query/results${searchFilter.toString()}&${queryString.stringify(
            normalizedFilter,
          )}`,
        { extra: { cancelToken } },
      );
    }

    // Array filters (branchs, campaigns, profiles, ...) go in the POST body so
    // large branch lists no longer overflow the URL and trigger an nginx 414.
    // Scalar params (page, page_size, lang, search) stay in the query string.
    return post(webApi + `api/results/filter${searchFilter.toString()}`, normalizedFilter, { extra: { cancelToken } });
  },
  getUserPlayerInformation: (id_invitation) =>
    get(webApi + `api/invitations/${id_invitation}`),
  createPerson: (body) => post(webApi + 'api/person/', body),
  updatePerson: (id, person) => put(webApi + 'api/person/' + id + '/', person),
  patchUser: (id, user) => patch(webApi + 'api/users/' + id + '/', user),
  deletePerson: (id) => deleteRequest(webApi + 'api/person/' + id + '/'),
  getAllProfiles: (filter, search = '') =>
    get(
      webApi +
        `api/profiles` +
        filter.toString() +
        `&lang=${localStorage.getItem('language')}&search=${search}`,
    ),
  getAllPartners: (filter) =>
    get(
      webApi +
        `api/partner` +
        filter.toString() +
        `&lang=${localStorage.getItem('language')}`,
    ),
  demographicFilter: (latlng) =>
    get(webApi + `api/demographic_filter/${latlng.lat}/${latlng.lng}`),
  getJobCompatibility: (body) => {
    let id_results = body.results;
    let queryObject = {
      id_results,
      id_positions: body.positions,
    };
    return get(
      webApi +
        `api/results/reports/profile?lang=${localStorage.getItem(
          'language',
        )}&${queryString.stringify(queryObject)}`,
    );
  },
  getCapabilities: () => {},
  getCountries: () =>
    get(
      webApi +
        `api/countries?page_size=250&lang=${localStorage.getItem('language')}`,
    ),
  getCities: (country_id) =>
    get(
      webApi +
        `api/cities?country_id=${country_id}&lang=${localStorage.getItem(
          'language',
        )}`,
    ),
  downloadProfileReport: (ids, reportType) => {
    let id_result = ids.map((res) => res.id_result);
    let id_invitations = ids.map((res) => res.id_invitation);
    // Sent as a POST body: one id per selected person overflows the URL length
    // limit on bulk downloads. No lang: the backend uses the account language.
    return post(
      webApi + 'api/results/reports/download',
      {
        id_result,
        id_invitations,
        type: reportType,
      },
      { extra: { responseType: 'blob' } },
    );
  },
  sendPersonReportEmail: (value, id_invitation, campaign) => {
    const queryObject = {
      id_result: value,
      id_invitation,
      campaign,
      type: 'individual',
    };

    return get(
      webApi + `api/results/reports/send?${queryString.stringify(queryObject)}`,
    );
  },
  getProfile: (id) =>
    get(
      webApi + `api/profiles?id=${id}&lang=${localStorage.getItem('language')}`,
    ),
  migrateCampaign: (body) =>
    post(
      webApi + `api/migrate_campaign/?lang=${localStorage.getItem('language')}`,
      body,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    ),
  exportTrend: (body) => {
    let id_results = body.results;
    let id_skills = false;
    let id_positions = false;
    body.capabilities
      ? (id_skills = body.data.map((id_sk) => id_sk))
      : (id_positions = body.data.map((id_pos) => id_pos));

    const response = body.capabilities
      ? post(
          webApi + `api/results/reports/skills/download`,
          {
            id_results,
            id_skills,
            lang: localStorage.getItem('language'),
          },
          {
            extra: { responseType: 'blob' },
          },
        )
      : post(
          webApi + `api/results/reports/profile/download`,
          {
            id_results,
            id_positions,
            lang: localStorage.getItem('language'),
          },
          {
            extra: { responseType: 'blob' },
          },
        );
    return response;
  },

  addNote: ({ description, player_id, invitation_id }) =>
    post(webApi + 'api/notes', { description, player_id, invitation_id }),

  updateNote: ({ featured, player_id, note_id }) =>
    patch(webApi + `api/notes/${note_id}`, { player_id, featured }),

  deleteNote: (note_id) => deleteRequest(webApi + `api/notes/${note_id}`),

  getNotes: (id_user_player) =>
    get(webApi + `api/notes?id_user_player=${id_user_player}`),

  addCV: ({ id_user_player, curriculum }) => {
    const formData = new FormData();
    curriculum.map((file) => {
      formData.append('id_user_player', id_user_player);
      formData.append('file', file ? file : null);
      formData.append('directory', file.type);
    });

    return post(webApi + 'api/files', formData, {
      headers: {
        'Content-Type': `multipart/form-data`,
      },
    });
  },

  getCV: (id_user_player) => {
    return get(webApi + `api/files?id_user_player=${id_user_player}`);
  },

  deleteAttached: ({ id_user_player, fileId }) => {
    return deleteRequest(
      webApi + `api/files?id_user_player=${id_user_player}&fileId=${fileId}`,
    );
  },

  // updateCV: ({ id, curriculum }) => {
  //   const formData = new FormData();
  //   formData.append('file', curriculum ? curriculum : null);
  //   return patch(webApi + `api/files/${id}`, formData);
  // },

  shareFile: ({ id_invitation, sendCv, sendNotes, emails }) => {
    return post(webApi + `api/companies/send-file`, {
      id_invitation,
      sendCv,
      sendNotes,
      emails,
      time_zone: dayjs.tz.guess(),
    });
  },
  addIeInvitation: (id_campaign, email) => {
    return post(webApi + 'api/invitations', {
      id_campaign,
      emails: [email],
      type: 'IE',
    });
  },

  massiveIeInvitation: (persons) => {
    return post(webApi + 'api/invitations', {
      persons: persons,
      type: 'IE',
      massive: true,
    });
  },

  getInstitutions: (search = '') => {
    const params = new URLSearchParams({ page: '0' });
    if (search) {
      params.append('search', search);
    }
    return get(
      settings.webApiCore + `/api/ines/institutions?${params.toString()}`,
    );
  },

  getDiscGap: (idResults, positionId, skillIds) =>
    post(webApi + 'api/results/reports/disc/gap', {
      id_results: idResults,
      positionId,
      skillIds,
    }),

  getGroupArts: (idResults) =>
    post(webApi + 'api/results/reports/disc/group-arts', {
      id_results: idResults,
    }),
};
